import type {
	DatovkaApiType,
	DatovkaApiTypeCombinationsWithPrefix,
} from '@gov-nx/api';
import type { ExtraConfig } from '@gov-nx/core/app';
import { ServiceCode } from '@gov-nx/module/service';

export const datovkaApiTypeCombinations: DatovkaApiTypeCombinationsWithPrefix =
	{
		FO: ['FO'],
		PO: ['PO', 'PO_REQ'],
		PFO: [
			'PFO',
			'PFO_ADVOK',
			'PFO_DANPOR',
			'PFO_INSSPR',
			'PFO_AUDITOR',
			'PFO_ZNALEC',
			'PFO_TLUMOCNIK',
			'PFO_ARCH',
			'PFO_AIAT',
			'PFO_AZI',
			'PFO_REQ',
		],
	};

const typesPO = (code: ServiceCode): DatovkaApiType['typSchranky'][] => {
	switch (code) {
		// FO ONLY
		case ServiceCode['vypis-bodoveho-hodnoceni-ridice']:
		case ServiceCode['vypis-vyuziti-udaju-z-ROB']:
		case ServiceCode['vypis-udaju-z-ROB']:
		case ServiceCode['vypis-z-rejstriku-trestu-FO']: // nema tlactko pro odeslani datovkou
		case ServiceCode['zadost-o-poskytovani-udaju']:
		case ServiceCode['volicsky-prukaz']:
			return ['FO'];

		// PFO ONLY
		case ServiceCode['neverejny-vypis-z-ROS']:
			return ['PFO'];

		// PO + PFO
		case ServiceCode['neverejny-vypis-z-ZR']: // nema tlactko pro odeslani datovkou
		case ServiceCode['vypis-vyuziti-udaju-z-ROS']:
			return ['PO', 'PFO'];

		// FO + PFO*
		case ServiceCode['podani-zadosti-o-informace']:
		case ServiceCode['potvrzeni-o-studiu']:
		case ServiceCode['trestni-oznameni']:
		case ServiceCode['verejny-vypis-z-ROS']:
		case ServiceCode['vypis-z-IR']:
		case ServiceCode['vypis-z-rejstriku-trestu-PO']:
		case ServiceCode['vypis-z-verejneho-rejstriku']:
		case ServiceCode['vypis-z-zivnostenskeho-rejstriku']:
		case ServiceCode['vypis-ze-seznamu-KD']:
		case ServiceCode['zadost-o-korespondencni-hlasovani']:
			return ['FO', ...(datovkaApiTypeCombinations['PFO'] ?? [])];

		// FO + PO* + PFO*
		case ServiceCode['zadost-o-osvedceni-DIG-ukonu']:
			return [
				'FO',
				...(datovkaApiTypeCombinations['PO'] ?? []),
				...(datovkaApiTypeCombinations['PFO'] ?? []),
			];
		default:
			return [];
	}
};

const typesPP = (code: ServiceCode): DatovkaApiType['typSchranky'][] => {
	switch (code) {
		// FO ONLY
		case ServiceCode['vypis-bodoveho-hodnoceni-ridice']:
		case ServiceCode['vypis-vyuziti-udaju-z-ROB']:
		case ServiceCode['vypis-udaju-z-ROB']:
		case ServiceCode['vypis-z-rejstriku-trestu-FO']: // nema tlactko pro odeslani datovkou
		case ServiceCode['zadost-o-poskytovani-udaju']:
		case ServiceCode['volicsky-prukaz']:
			return ['FO'];

		// PFO* ONLY (including all PFO subtypes)
		case ServiceCode['neverejny-vypis-z-ROS']:
			return [...(datovkaApiTypeCombinations['PFO'] ?? [])];

		// PO + PFO
		case ServiceCode['neverejny-vypis-z-ZR']: // nema tlactko pro odeslani datovkou
			return ['PO', 'PFO'];

		// PO + PFO*
		case ServiceCode['vypis-vyuziti-udaju-z-ROS']:
			return ['PO', ...(datovkaApiTypeCombinations['PFO'] ?? [])];

		// FO + PFO*
		case ServiceCode['potvrzeni-o-studiu']:
		case ServiceCode['trestni-oznameni']:
		case ServiceCode['zadost-o-korespondencni-hlasovani']:
			return ['FO', ...(datovkaApiTypeCombinations['PFO'] ?? [])];

		// FO + PO* + PFO*
		case ServiceCode['zadost-o-osvedceni-DIG-ukonu']:
		case ServiceCode['vypis-z-verejneho-rejstriku']:
		case ServiceCode['vypis-ze-seznamu-KD']:
		case ServiceCode['vypis-z-IR']:
		case ServiceCode['verejny-vypis-z-ROS']:
		case ServiceCode['vypis-z-rejstriku-trestu-PO']:
		case ServiceCode['vypis-z-zivnostenskeho-rejstriku']:
		case ServiceCode['podani-zadosti-o-informace']:
			return [
				...(datovkaApiTypeCombinations['FO'] ?? []),
				...(datovkaApiTypeCombinations['PO'] ?? []),
				...(datovkaApiTypeCombinations['PFO'] ?? []),
			];

		default:
			return [];
	}
};

export const getRequiredDataBoxes = (
	code: ServiceCode,
	application: ExtraConfig['application']
): DatovkaApiType['typSchranky'][] => {
	switch (application) {
		case 'po':
		case 'gov':
			return typesPO(code);
		case 'pp':
			return typesPP(code);
		default:
			return [];
	}
};

export const getAllDataboxTypes = (): DatovkaApiType['typSchranky'][] => [
	...(datovkaApiTypeCombinations['FO'] ?? []),
	...(datovkaApiTypeCombinations['PFO'] ?? []),
	...(datovkaApiTypeCombinations['PO'] ?? []),
];
